import { isStandardSetting, sanitizeOpexSettings, settingsTemplateOptions, type OpexSettings, type OpexSettingsTemplate } from "~/helpers/modules/opex-helpers";
import type { NamespaceOpex } from "~/helpers/user-preferences-helpers";

export const useOpexSavedTemplates = (isModule: boolean) => {
  const t = useTranslator();

  const preferencesQuery = usePreferencesNamespace(isModule ? "preferencesNamespaceOpex" : "preferencesNamespacePortfolioOpex");

  const preferences = computed(() => preferencesQuery.result.value);

  const loading = ref(false);

  /** Keeping a reference of templates while preferences are updating */
  const customSavedTemplates = ref<OpexSettingsTemplate[]>([]);

  watch(
    preferences,
    (v) => {
      if (preferencesQuery.loading.value) return;

      customSavedTemplates.value = v?.savedTemplates ?? [];
    },
    { immediate: true }
  );

  const allSavedTemplates = computed<OpexSettingsTemplate[]>(() => [...settingsTemplateOptions, ...customSavedTemplates.value]);

  const nextId = computed(() =>
    customSavedTemplates.value.reduce((current, template) => {
      const next = parseInt(template.id);

      return next >= current ? next + 1 : current;
    }, 1)
  );

  const _save = async (newTemplatesList: OpexSettingsTemplate[]) => {
    if (!preferences.value) return;

    loading.value = true;

    await preferencesQuery.update({ ...preferences.value, savedTemplates: newTemplatesList.filter((t) => !isStandardSetting(t)) });

    loading.value = false;
  };

  const createTemplate = async (name: string, settings: OpexSettings | NamespaceOpex) => {
    const id = nextId.value.toString();
    const newSavedTemplates = [...customSavedTemplates.value, { id, name, settings: sanitizeOpexSettings(settings) }];

    await _save(newSavedTemplates);

    return id;
  };

  const updateTemplate = async (id: string, name: string, settings: OpexSettings | NamespaceOpex): Promise<undefined> => {
    const newSavedTemplates = customSavedTemplates.value.slice();
    const index = newSavedTemplates.findIndex((template) => template.id === id);

    if (index === -1) return;

    newSavedTemplates[index] = {
      id,
      name,
      settings: sanitizeOpexSettings(settings),
    };

    await _save(newSavedTemplates);
  };

  const deleteTemplate = async (id: string) => await _save(customSavedTemplates.value.filter((template) => template.id !== id) ?? []);

  const readTemplate = async (id: string) => {
    if (!preferences.value) return;

    if (preferences.value.templateModified || preferences.value.template !== id)
      await preferencesQuery.update({ ...preferences.value, template: id, templateModified: false });
  };

  return {
    result: allSavedTemplates,
    loading,
    create: createTemplate,
    read: readTemplate,
    update: updateTemplate,
    delete: deleteTemplate,
  };
};
